import { useEffect, useState } from 'react';
import { InnerPageToggleButton } from '@wajeez-web/components/ui';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import {
  Card,
  Button,
  CallBackSelector,
  HorizontalTabs,
} from '@wajeez-web/components/ui';
import { WajeezList, WajeezRow } from '@wajeez-web/components/list-builder';
import { BsPlusCircleFill, BsPlus } from 'react-icons/bs';
import ContentEntityFormModal from './ContentEntityFormModal';
import { RiDeleteBinLine, RiExternalLinkFill } from 'react-icons/ri';
import { BiEditAlt, BiLoaderAlt } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';
import {
  HasPermissionWrapperComponent,
  HasPermission,
} from '@wajeez-web/components/helpers';
import {
  useContentSelector,
  ContentEntityActions,
  ContentEntityTextsActions,
  ContentEntityAudiosActions,
  ContentEntityTypeActions,
  createContentEntityMultilingual,
  editContentEntityMultilingual,
  getContentEntityDetailsByIdMultilingual,
  getContentEntityItemDetailsByIdMultilingual,
} from '@wajeez-web/stores/content';
import {
  Languages,
  useCommonDispatch,
  useCommonSelector,
} from '@wajeez-web/stores/common';
import { Server } from '@wajeez-web/core/api-base';
import PreviewCollection from './PreviewCollection';
import { DeleteItemAlert } from '@wajeez-web/components/ui';
import { AiOutlineCheck, AiOutlineClose } from 'react-icons/ai';
import { useTranslation } from 'react-i18next';
interface ContentEntityCollectionComponentProps {
  data: Server.ContentEntityTypeLookUpWithFieldsOutput;
  id: string;
  type: string;
  isPublished: boolean | undefined;
  showTypeOnItems?: boolean;
}

function arrayEquals(
  a: Server.ContentEntityOutput[],
  b: Server.ContentEntityOutput[]
) {
  return (
    Array.isArray(a) &&
    Array.isArray(b) &&
    a.length === b.length &&
    a.every((val, index) => val.id === b[index].id)
  );
}

const ContentEntityCollectionComponent = ({
  data,
  id,
  type,
  isPublished,
  showTypeOnItems,
}: ContentEntityCollectionComponentProps) => {
  const navigate = useNavigate();

  const dispatch = useCommonDispatch();

  const [orderingList, setOrderingList] = useState<
    Server.ContentEntityOutput[]
  >([]);
  const [selectorValue, setSelectorValue] = useState<
    | {
        label: string;
        value: string;
      }
    | undefined
    | null
  >(null);
  const [contentFormState, setContentFormState] = useState<{
    show: boolean;
    id?: string;
  }>({ show: false });
  const [isNoChapterPublished, setIsNoChapterPublished] = useState(true);
  const [isOrderSubmitLoading, setIsOrderSubmitLoading] = useState(false);

  const {
    contentColections: contentColectionsObject,
    contentColectionsEnglish: contentColectionsEnglishObject,
    collectionStatus,
    updatingStatus,
    assignStatus,
    createMultilingual,
    editMultilingual,
    publishStatus,
  } = useContentSelector((s) => s.contentEntity);

  const { modalDetails } = useContentSelector((s) => s.contentEntityType);

  const { t } = useTranslation();

  const languageId = useCommonSelector(
    ({ common }) => common.settings.LanguageId
  );

  const selectedContentCollectionsObject =
    languageId === Languages.ar
      ? contentColectionsObject
      : contentColectionsEnglishObject;

  const contentColections = selectedContentCollectionsObject[
    data.name || 'all'
  ] as Server.ContentEntityWithOrderOutput[] | undefined;

  const saveOrdering = () => {
    setIsOrderSubmitLoading(true);

    Promise.all(
      orderingList.map((item, index) =>
        dispatch(
          ContentEntityActions.orderItemAsync({
            order: index,
            itemId: item.id,
            collectionId: id,
            languageId: languageId || 1,
          })
        )
      )
    ).then(() => {
      setIsOrderSubmitLoading(false);

      getData();
    });
  };

  const getData = () => {
    dispatch(
      ContentEntityActions.getContentCollectionAsync({
        ContenteEntityId: id,
        page: 1,
        pageSize: 500,
        ContenteEntityType: data.name,
        languageId: languageId?.toString(),
      })
    );

    dispatch(
      ContentEntityActions.getContentCollectionEntityWithMediaAsync({
        ContenteEntityId: id,
        page: 1,
        pageSize: 500,
        ContenteEntityType:
          !data.name || data.name === 'CONTENTELEMENTS' ? undefined : data.name,
      })
    );
  };

  const addItem = async () => {
    if (contentColections) {
      let max = Math.max(...contentColections.map((g) => g.order ?? -1));
      max = max < 0 ? 0 : max;

      const res = await dispatch(
        ContentEntityActions.assignContentEntityAsync({
          itemId: selectorValue?.value,
          collectionId: id,
          languageId: languageId || 1,
          order: max == -1 ? (contentColections.length || 0) * 100 : max + 100,
        })
      );
      if (res.meta.requestStatus === 'fulfilled') {
        getData();
        setSelectorValue(undefined);
      }
    }
  };

  useEffect(() => {
    if (contentColections) {
      for (const cc of contentColections) {
        if (cc.published) {
          setIsNoChapterPublished(false);

          return;
        }
      }

      setIsNoChapterPublished(true);
    }
  }, [contentColections]);

  useEffect(() => {
    if (id) {
      getData();
    }
  }, [id]);

  useEffect(() => {
    contentColections && setOrderingList(contentColections);
  }, [contentColections]);

  const openModal = () => {
    dispatch(
      ContentEntityTypeActions.getContentEntityTypeByNameForModalAsync({
        name: !data.name || data.name === 'CONTENTELEMENTS' ? '' : data.name,
      })
    ).then(() => {
      setContentFormState({ show: true });
    });
  };

  const onOrderingChange = (list: Server.ContentEntityOutput[]) => {
    setOrderingList(list);
  };

  const onSearch = async (searchInput: string) => {
    return ContentEntityActions.searchContentEntityPaginatedForSelector({
      ContentEntityTypeNameSearchText: searchInput,
      ContentItemType:
        !data.name || data.name === 'CONTENTELEMENTS' ? '' : data.name,
      page: 1,
      pageSize: 100,
    }).then((res) => {
      return res?.map((item) => {
        const authors = item.authors;

        return {
          label:
            item.title +
            (authors?.length || 0 > 1
              ? ` (${authors?.join(', ')})`
              : authors?.[0]
              ? ` (${authors?.[0]})`
              : ''),
          value: item.id,
        };
      });
    });
  };

  const getContentTexts = async (id: string) => {
    await dispatch(
      ContentEntityTextsActions.getContentEntityTextAsync({
        ContentEntityId: id,
        page: 1,
        pageSize: 100,
      })
    );
  };

  const getContentAudios = async (id: string) => {
    await dispatch(
      ContentEntityAudiosActions.getAllContentEntityAudiosAsync({
        ContentEntityId: id,
        page: 1,
        pageSize: 100,
      })
    );
  };

  const onSubmitModal = async (
    data: Server.CreateContentEntityByTypeIdV2Input
  ) => {
    if (data) {
      if (!contentFormState.id) {
        createContentEntityMultilingual(data, (response) => {
          if (contentColections) {
            let max = Math.max(...contentColections.map((g) => g.order ?? -1));
            max = max < 0 ? 0 : max;

            dispatch(
              ContentEntityActions.assignContentEntityAsync({
                languageId: 1,
                order: max == -1 ? contentColections.length * 100 : max + 100,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                itemId: response || '',
                collectionId: id,
              })
            ).then((res) => {
              if (res.meta.requestStatus === 'fulfilled') {
                setContentFormState({ show: false });
                getData();
              }
            });
          }
        });
      } else {
        const mappedData = {
          ...data,
          profiles: data.profiles.map((p) => {
            const defaultNarrator = p.narratorAudios?.find((n) => n.isDefault);

            return {
              ...p,
              narratorAudios: p.narratorAudios?.map((n, i) => {
                if (i === 0 && !defaultNarrator) {
                  return {
                    ...n,
                    isDefault: true,
                  };
                } else {
                  return n;
                }
              }),
            };
          }),
        };

        editContentEntityMultilingual(
          { ...mappedData, id: contentFormState.id || id },
          (response) => {
            dispatch(ContentEntityAudiosActions.clear());
            dispatch(ContentEntityTextsActions.clear());
            dispatch(ContentEntityActions.clearSelectedCollectionData());

            getData();

            setContentFormState({ show: false });
          }
        );
      }
    }
  };

  const onCloseModal = () => {
    setContentFormState({ show: false });
    dispatch(ContentEntityAudiosActions.clear());
    dispatch(ContentEntityTextsActions.clear());
    dispatch(ContentEntityActions.clearSelectedCollectionData());
  };

  const onEdit = async (id: string) => {
    console.log(id);

    await dispatch(
      ContentEntityTypeActions.getContentEntityTypeByNameForModalAsync({
        name: !data.name || data.name === 'CONTENTELEMENTS' ? '' : data.name,
      })
    );

    setContentFormState({ show: true, id: id });
  };

  const isAddDisabled = isPublished && !data.canBeAssignedWhenPublished;
  const isReorderingDisabled =
    !data.canBeAssignedWhenPublished && !isNoChapterPublished;

  const getModifyContent = () => {
    return (
      <div>
        {' '}
        <div className="flex space-s-2 align-middle">
          <div className="w-full">
            <CallBackSelector
              value={selectorValue || undefined}
              inputValue={selectorValue?.label || ''}
              key={data.name}
              onSearch={onSearch}
              onChange={(name, value: any) => {
                setSelectorValue(value);
              }}
              name={data.name || ''}
              isLoading={assignStatus === 'loading'}
              disabled={isAddDisabled}
            />{' '}
          </div>
          <HasPermissionWrapperComponent permissions={['28.1']}>
            <Button
              type="text"
              color="primary"
              onClick={() => {
                addItem && addItem();
              }}
              Icon={BsPlusCircleFill}
              disabled={!selectorValue || isAddDisabled}
            />
          </HasPermissionWrapperComponent>
        </div>
        <div className=" ">
          <WajeezList
            isLoading={collectionStatus === 'loading'}
            gridItems={3}
            onOrder={HasPermission('28.5') ? onOrderingChange : undefined}
            data={contentColections || []}
            keyExtractor={(item) => item.id + ''}
            disabledSortable={isAddDisabled || isReorderingDisabled}
            renderItem={(item, i) => (
              <WajeezRow
                key={item.id}
                title={
                  <div
                    onClick={() =>
                      data.name &&
                      data.name !== 'CONTENTELEMENTS' &&
                      onEdit(item.id || '')
                    }
                    className={`w-full py-6 px-4 flex space-s-2 items-center ${
                      data.name &&
                      data.name !== 'CONTENTELEMENTS' &&
                      'cursor-pointer'
                    }`}
                  >
                    <div>
                      {i + 1}. {item.title}{' '}
                      {showTypeOnItems ? `(${item.typeDisplayName})` : ''}
                    </div>
                    {item.published ? (
                      <AiOutlineCheck className="text-green-500" />
                    ) : (
                      <AiOutlineClose className="text-red-500" />
                    )}
                  </div>
                }
                paddingStyle="p-0"
                widthStyleTitle="w-full"
                endSideComponents={[
                  <div className="w-[75px] flex justify-center">
                    {item.id && publishStatus[item.id] !== 'loading' ? (
                      <Button
                        disabled={HasPermission('27.24') ? false : true}
                        type="text"
                        shape="circle"
                        color="textSecondary"
                        bg="gray-500/10"
                        title={item.published ? 'Unpublish' : 'Publish'}
                        onClick={async () => {
                          const res = await dispatch(
                            ContentEntityActions.setPublishAsync({
                              id: item.id,
                              published: !item.published,
                              languageId: item.languageId,
                            })
                          );
                          if (res.meta.requestStatus === 'fulfilled') {
                            getData();
                          }
                        }}
                      />
                    ) : (
                      <BiLoaderAlt className="animate-spin w-5" />
                    )}
                  </div>,
                  <Button
                    type="text"
                    shape="circle"
                    color="textSecondary"
                    bg="gray-500/10"
                    disabled={HasPermission('28.4') ? false : true}
                    Icon={RiDeleteBinLine}
                    onClick={() => {
                      DeleteItemAlert(() => {
                        return dispatch(
                          ContentEntityActions.unassignContentEntityAsync({
                            itemId: item.id,
                            collectionId: id,
                            languageId: 1,
                          })
                        ).then(() => getData());
                      }, t('general.will delete') + item.title);
                    }}
                  />,
                  data.name && data.name !== 'CONTENTELEMENTS' ? (
                    <Button
                      disabled={HasPermission('27.2') ? false : true}
                      type="text"
                      shape="circle"
                      color="textSecondary"
                      bg="gray-500/10"
                      Icon={BiEditAlt}
                      onClick={() => onEdit(item.id || '')}
                    />
                  ) : null,
                ].concat(
                  data.contentItemType === 1
                    ? [
                        <Button
                          type="text"
                          shape="circle"
                          color="textSecondary"
                          bg="gray-500/10"
                          Icon={RiExternalLinkFill}
                          onClick={() => {
                            navigate(
                              `type/${item.contentEntityType}/${item.id}/0`
                            );
                          }}
                        />,
                      ]
                    : []
                )}
              />
            )}
            containerType="rows"
            getData={getData}
          />
        </div>
      </div>
    );
  };

  return (
    <Card>
      <div className="space-y-8">
        <div className="flex justify-between">
          <h3 className="ms-2 lg:ms-0 text-2xl flex items-center align-middle font-bold space-s-2 tracking-tight leading-none">
            {<InnerPageToggleButton />}{' '}
            <div className="">
              <ReturnTranslatedText
                text={`general.${data.displayNamePlural}`}
                origintext={data.displayNamePlural}
              />{' '}
            </div>
          </h3>
          {(contentColections?.length || 0) > 0 &&
          !arrayEquals(orderingList, contentColections || []) ? (
            <HasPermissionWrapperComponent permissions={['28.5']}>
              <div className="flex space-s-2">
                <Button
                  title={'Save'}
                  type="primary"
                  onClick={saveOrdering}
                  loading={isOrderSubmitLoading}
                  disabled={isAddDisabled || isReorderingDisabled}
                />
                <Button
                  title={'Cancel'}
                  type="default"
                  onClick={() => {
                    getData();
                  }}
                />
              </div>
            </HasPermissionWrapperComponent>
          ) : (
            data.name &&
            data.name !== 'CONTENTELEMENTS' && (
              <HasPermissionWrapperComponent permissions={['27.1']}>
                <Button
                  bg="primary"
                  color="white"
                  title={'Add new'}
                  Icon={BsPlus}
                  onClick={() => openModal()}
                  disabled={isAddDisabled}
                />
              </HasPermissionWrapperComponent>
            )
          )}
        </div>
        {data.requireAudio || data.requireText ? (
          <HorizontalTabs
            tabs={{
              Modify: getModifyContent(),
              Preview: (
                <PreviewCollection
                  getContentAudios={getContentAudios}
                  getContentTexts={getContentTexts}
                  modalDetails={modalDetails}
                  isLoading={
                    updatingStatus === 'loading' || assignStatus === 'loading'
                  }
                  onEdit={onEdit}
                  setContentFormState={setContentFormState}
                  onClose={onCloseModal}
                  onSubmit={onSubmitModal}
                  contentFormState={contentFormState}
                />
              ),
            }}
          />
        ) : (
          getModifyContent()
        )}
        {contentFormState.show && modalDetails && (
          <ContentEntityFormModal
            isOpen={contentFormState.show}
            loading={
              createMultilingual.status === 'loading' ||
              editMultilingual.status === 'loading' ||
              assignStatus === 'loading'
            }
            onClose={onCloseModal}
            data={modalDetails}
            onSubmit={onSubmitModal as any}
            id={contentFormState.id}
          />
        )}
      </div>
    </Card>
  );
};

export default ContentEntityCollectionComponent;
